"use client"

import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react"
import type { LandRiskState, SelectedLocation, UserRole, PackageTier, RiskResults, Rating, UserProfile } from "./types"

interface LandRiskContextType extends LandRiskState {
  setLocation: (location: SelectedLocation) => void
  setUserRole: (role: UserRole) => void
  setPackageTier: (tier: PackageTier) => void
  setResults: (results: RiskResults) => void
  setLocationConfirmed: (confirmed: boolean) => void
  setRating: (rating: Rating) => void
  setUserProfile: (profile: UserProfile | null) => void
  reset: () => void
}

const STORAGE_KEY = "landrisk-state"

const initialState: LandRiskState = {
  location: null,
  userRole: "buyer",
  packageTier: null,
  results: null,
  locationConfirmed: false,
  rating: null,
  userProfile: null,
}

function normalizeRiskResults(results: RiskResults): RiskResults {
  return {
    ...results,
    floodRisk: {
      ...results.floodRisk,
      historicalFloodEvents:
        typeof results.floodRisk?.historicalFloodEvents === "number" ? results.floodRisk.historicalFloodEvents : 0,
      historicalFloodEventDetails: Array.isArray(results.floodRisk?.historicalFloodEventDetails)
        ? results.floodRisk.historicalFloodEventDetails.map((event) => ({
            date: typeof event?.date === "string" ? event.date : "Unknown date",
            newsCoverage: typeof event?.newsCoverage === "string" ? event.newsCoverage : null,
            links: Array.isArray(event?.links)
              ? event.links
                  .filter((link) => typeof link?.label === "string" && typeof link?.url === "string")
                  .map((link) => ({ label: link.label, url: link.url }))
              : [],
          }))
        : [],
      riparianZone: Boolean(results.floodRisk?.riparianZone),
      nearbyWaterBodyName: typeof results.floodRisk?.nearbyWaterBodyName === "string"
        ? results.floodRisk.nearbyWaterBodyName
        : null,
      nearbyWaterBodyType: typeof results.floodRisk?.nearbyWaterBodyType === "string"
        ? results.floodRisk.nearbyWaterBodyType
        : null,
    },
    legalRisk: {
      ...results.legalRisk,
      schoolZoneOverlap: Boolean(results.legalRisk?.schoolZoneOverlap),
      schoolName: typeof results.legalRisk?.schoolName === "string" ? results.legalRisk.schoolName : null,
      roadReserveOverlap: Boolean(results.legalRisk?.roadReserveOverlap),
      roadName: typeof results.legalRisk?.roadName === "string" ? results.legalRisk.roadName : null,
    },
  }
}

function normalizeLandRiskState(state: LandRiskState): LandRiskState {
  return {
    ...state,
    results: state.results ? normalizeRiskResults(state.results) : null,
  }
}

function loadState(): LandRiskState {
  if (typeof window === "undefined") return initialState
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) return normalizeLandRiskState(JSON.parse(stored) as LandRiskState)
  } catch {
    // ignore
  }
  return initialState
}

function saveState(state: LandRiskState) {
  if (typeof window === "undefined") return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore
  }
}

export function persistLandRiskState(state: LandRiskState) {
  saveState(state)
}

export function loadPersistedLandRiskState(): LandRiskState {
  return loadState()
}

const LandRiskContext = createContext<LandRiskContextType | undefined>(undefined)

export function LandRiskProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LandRiskState>(initialState)
  const [hydrated, setHydrated] = useState(false)

  // Hydrate from sessionStorage on mount
  useEffect(() => {
    setState(loadState())
    setHydrated(true)
  }, [])

  // Persist to sessionStorage on every change after hydration
  useEffect(() => {
    if (hydrated) {
      saveState(state)
    }
  }, [state, hydrated])

  const setLocation = useCallback((location: SelectedLocation) => {
    setState((prev) => ({ ...prev, location }))
  }, [])

  const setUserRole = useCallback((role: UserRole) => {
    setState((prev) => ({ ...prev, userRole: role }))
  }, [])

  const setPackageTier = useCallback((tier: PackageTier) => {
    setState((prev) => ({ ...prev, packageTier: tier }))
  }, [])

  const setResults = useCallback((results: RiskResults) => {
    setState((prev) => ({ ...prev, results }))
  }, [])

  const setLocationConfirmed = useCallback((confirmed: boolean) => {
    setState((prev) => ({ ...prev, locationConfirmed: confirmed }))
  }, [])

  const setRating = useCallback((rating: Rating) => {
    setState((prev) => ({ ...prev, rating }))
  }, [])

  const setUserProfile = useCallback((profile: UserProfile | null) => {
    setState((prev) => ({ ...prev, userProfile: profile }))
  }, [])

  const reset = useCallback(() => {
    setState(initialState)
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  const value = useMemo(
    () => ({
      ...state,
      setLocation,
      setUserRole,
      setPackageTier,
      setResults,
      setLocationConfirmed,
      setRating,
      setUserProfile,
      reset,
    }),
    [
      state,
      setLocation,
      setUserRole,
      setPackageTier,
      setResults,
      setLocationConfirmed,
      setRating,
      setUserProfile,
      reset,
    ]
  )

  return (
    <LandRiskContext value={value}>
      {children}
    </LandRiskContext>
  )
}

export function useLandRisk() {
  const context = useContext(LandRiskContext)
  if (!context) {
    throw new Error("useLandRisk must be used within a LandRiskProvider")
  }
  return context
}
